import { min, max } from 'd3-array';
import { deepClone } from '../util/deepClone';

import Indicator from '../Indicator';
import BarMarker from '../markers/BarMarker';

/** Normalized Relative Volume indicator */
export default class NRVIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {string} markerType - Indicator marker type - 'default' | 'posneg'
	 */
	_getDefaultParams() {
		return {
			id: 'nrv',
			name: 'Normalized Relative Volume',
			description:
				'Relative volume compared to an index, and normalized using average historical data',
			inputs: [
				{ name: 'comparisonsymbol', value: '593933' },
				{ name: 'longperiod', value: 125 },
				{ name: 'shortperiod', value: 1 }
			],
			markerType: 'posneg', // default, posneg
			valueMultiplier: 1
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		const dataCopy = deepClone(data);
		const panelRange = this.panel.xAxis.primaryScale.domain();

		// Remove first element when returnPreviousPoint is selected so panel size is not affected
		if (this.panel.core.params.returnPreviousPoint) {
			dataCopy.nrv.shift();
		}

		//Remove last element if it is out of view in panel so range size is not affected
		if (dataCopy.nrv[dataCopy.nrv.length - 1].dateIndex > panelRange[1]) {
			dataCopy.nrv.pop();
		}

		return this._min(
			0,
			min(dataCopy.nrv || [], i => +i.nrv)
		);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		// Remove first and last elements so panel size is not affected
		const dataCopy = deepClone(data);
		const panelRange = this.panel.xAxis.primaryScale.domain();

		// Remove first element when returnPreviousPoint is selected so panel size is not affected
		if (this.panel.core.params.returnPreviousPoint) {
			dataCopy.nrv.shift();
		}

		//Remove last element if it is out of view in panel so range size is not affected
		if (dataCopy.nrv[dataCopy.nrv.length - 1].dateIndex > panelRange[1]) {
			dataCopy.nrv.pop();
		}

		return this._max(
			0,
			max(dataCopy.nrv || [], i => +i.nrv)
		);
	}

	/**
	 * @return {object}
	 * @property {BarMarker} bar
	 */
	_getMarkers() {
		return { bar: new BarMarker() };
	}

	/**
	 * Returns pos/neg/unchanged volume data based against the price dataset
	 * @param {object[]} data - Indicator data
	 * @param {object[]} dataPrice - Symbol price data
	 * @param {string} type - Type of volume data - 'pos' | 'neg' | 'unch'
	 */
	_getDataMapPosNeg(data, dataPrice, type) {
		// return pos/neg/unchanged volume data based against the price dataset
		switch (type) {
			case 'pos':
				return data.map((d, x) => {
					return [
						d.dateIndex,
						typeof dataPrice[x].diff === 'number' &&
						dataPrice[x].diff > 0
							? +d.nrv
							: 0
					];
				});

			case 'neg':
				return data.map((d, x) => {
					return [
						d.dateIndex,
						typeof dataPrice[x].diff === 'number' &&
						dataPrice[x].diff < 0
							? +d.nrv
							: 0
					];
				});

			case 'unch':
				return data.map((d, x) => {
					return [
						d.dateIndex,
						dataPrice[x].diff === 0 ? +d.nrv : 0
					];
				});

			default: return null;
		}
	}

	/** @param {object} data Symbol data object */
	render(data) {
		if (
			this.params.markerType === 'posneg' &&
			data.price &&
			data.nrv &&
			data.price.length &&
			data.nrv.length &&
			data.nrv[0].date - data.price[0].date === 0
		) {
			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				this._getDataMapPosNeg(data.nrv, data.price, 'pos'),
				this.xScale,
				this.yScale,
				this.params.style.positive.color,
				'bottom'
			);

			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				this._getDataMapPosNeg(data.nrv, data.price, 'neg'),
				this.xScale,
				this.yScale,
				this.params.style.negative.color,
				'bottom'
			);

			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				this._getDataMapPosNeg(data.nrv, data.price, 'unch'),
				this.xScale,
				this.yScale,
				this.params.style.neutral.color,
				'bottom'
			);
		} else {
			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				this._getDataMap(data.nrv),
				this.xScale,
				this.yScale,
				this.params.style.color,
				'bottom'
			);
		}
	}
}
